import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { routeSeo } from "@/content/seo";

const model = [
  {
    label: "Authenticated delegation",
    body: "An agent never acts as itself. Every operation is delegated by an authenticated human user and carries that user's authority, never more.",
  },
  {
    label: "External-agent principal",
    body: "A compliant AI agent is represented as a distinct, typed principal, never indistinguishable from a human clicking a mouse.",
  },
  {
    label: "Capability discovery",
    body: "Agents discover approved, catalogued operations by identifier, never a hidden or guessed action.",
  },
  {
    label: "Resource scopes",
    body: "Every request is bounded to explicit, granted resources, never open-ended access to everything in Devory.",
  },
  {
    label: "Operation submission and lifecycle",
    body: "Operations are submitted, tracked and resolved through a defined lifecycle, not fired and forgotten.",
  },
  {
    label: "Confirmation and approval",
    body: "Higher-risk operations require an explicit human confirmation boundary before they proceed.",
  },
  {
    label: "Audit envelopes",
    body: "Every operation an agent performs is wrapped in an auditable, traceable record.",
  },
  {
    label: "Revocation and fail-closed enforcement",
    body: "Access can be revoked at any time; when a check cannot be made, the operation is denied, not permitted.",
  },
];

const protocolFoundations = [
  "Capability protocol",
  "Resource-scope protocol",
  "Operation protocol",
  "Operation lifecycle",
  "Audit-envelope protocol",
  "External-agent principal foundation",
];

export function AgentUserInterfacePage() {
  const seo = routeSeo["/agent-user-interface"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/agent-user-interface" />

      <section className="container page-hero">
        <p className="eyebrow">For AI</p>
        <h1 className="page-hero-title">An AI agent needs its own identity, not yours</h1>
        <p className="hero-stage">
          <StatusBadge status="in-development" />
        </p>
        <p className="page-hero-lede">
          An agent acting through your API key is indistinguishable from you, which means nobody can
          later say what the agent actually did, or stop it without stopping yourself. Devory
          Autonomous User Interface, <strong>AUI</strong>, gives every AI agent a distinct,
          credentialed identity of its own. It is scoped to what its owner has authorised, revocable
          on its own, and never confused with the reasoning model doing the thinking behind it.
        </p>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="ai" size="xl" state="default" />
      </section>

      <section
        className="container-narrow section-tight prose"
        aria-labelledby="why-matters-heading"
      >
        <p className="eyebrow">Why this matters</p>
        <h2 id="why-matters-heading">Discovery instead of regeneration</h2>
        <p>
          AI coding assistants write code quickly, but speed does not remove the underlying problem:
          the same authentication systems, storage layers, notification pipelines and API
          integrations get generated again on almost every project. Each time, the assistant spends
          tokens and context solving something that has already been solved elsewhere, then more
          tokens debugging and refining what it just wrote. The output is rarely wrong so much as it
          is redundant, a new implementation of something that already existed, built again from
          nothing.
        </p>
        <p>
          Devory changes what an agent does with that effort. Instead of generating a payment
          integration from scratch, an agent can discover a qualified one in the Marketplace, read
          its interface, and compose it into the application it is building. That does not mean the
          agent stops writing code. It means the code it writes is the part that is actually
          specific to the problem at hand, while the solved majority is reused rather than rebuilt.
          In practice this tends to mean lower token consumption on repetitive implementation work,
          fewer repeated mistakes, and more consistent results across projects that draw from the
          same qualified components.
        </p>
      </section>

      <section className="container section-tight" aria-labelledby="what-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="what-heading">What AUI actually lets an agent do</span>}
            lede="A structured way to discover approved capabilities, request scoped resources, submit operations and return auditable outcomes, without ever becoming an independent user or an unrestricted system principal."
          />
          <ul className="list-check">
            <li>Not an unrestricted autonomous agent, and not an independent user</li>
            <li>Not a replacement for human authority: every action traces back to a real user</li>
            <li>Not a general-purpose chatbot</li>
            <li>Not an agent with access to everything in Devory</li>
            <li>
              Not production-ready or externally connectable without qualification and evidence
            </li>
          </ul>
        </GlassPanel>
      </section>

      <section className="container section" aria-labelledby="model-heading">
        <SectionHeading
          level={2}
          title={<span id="model-heading">The delegation and audit model</span>}
        />
        <div className="grid-3">
          {model.map((item) => (
            <GlassPanel as="article" key={item.label} className="audience-card">
              <h3>{item.label}</h3>
              <p>{item.body}</p>
            </GlassPanel>
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="foundations-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="foundations-heading">Protocol foundations</span>}
            lede="These are the contracts AUI is built on today. It is not yet an externally reachable, publicly connectable service."
          />
          <ul className="list-check">
            {protocolFoundations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight" aria-labelledby="mcp-heading">
        <GlassPanel className="problem-card">
          <div className="capability-card-head">
            <SectionHeading
              level={2}
              title={<span id="mcp-heading">MCP is the transport, not the innovation</span>}
              lede="MCP (Model Context Protocol) is a common language many AI hosts already speak, including Claude. It carries the request. What actually matters is what it connects to: trusted components, a qualified Marketplace and an accountable agent identity, which is what AUI provides."
            />
            <StatusBadge status="in-development" />
          </div>
          <p>
            An early, real MCP server exists today (<code>devory-mcp</code>) with two working,
            read-only tools: a live connectivity check, and an identity lookup authenticated with
            the connecting user's own Devory Cloud API key. Live-verified against production, not a
            mock. What it does not do yet: give an agent its own distinct External Agent Principal
            identity, scoped capabilities, or an audit trail separate from the user's own account --
            today it authenticates as the user, the same as any personal API script would. Broader,
            governed MCP tools connecting to the full delegation and audit model above are planned
            next, gated on the same qualification discipline as everything else on this page.
          </p>
        </GlassPanel>
      </section>

      <section className="container section-tight" aria-labelledby="aui-status-heading">
        <GlassPanel className="problem-card">
          <div className="capability-card-head">
            <SectionHeading level={2} title={<span id="aui-status-heading">Current status</span>} />
            <StatusBadge status="in-development" />
          </div>
          <p>
            Protocol foundations are implemented: capability discovery, resource-scope and operation
            contracts, confirmation gating and audit envelopes. No externally reachable network
            transport exists yet, so external agents cannot currently connect to a Devory deployment
            over the public internet. External pilot integration is in development and not available
            now.
          </p>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Building an agent integration?</h2>
          <p>
            Tell Devory what your agent needs to do. It directly shapes what AUI and MCP expose
            first.
          </p>
          <div className="cta-banner-actions">
            <CtaLink to="/marketplace" variant="secondary">
              See Agents and integrations in the Marketplace
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
